import type { InstallCertificateType } from '@/enums/Charger/Certificate/InstallCertificateType'
import { buildQueryParams, toQueryParams } from '@/helpers/ApiHelper'
import type { IPaginatedResponse } from '@/misc/IPaginatedResponse'
import type { IPaginationOptions } from '@/misc/IPaginationOptions'
import type { IChargerActionLogFilterParams } from '@/models/ActionLog/Filtering/IChargerActionLogFilterParams'
import type { IChargerActionLogs } from '@/models/ActionLog/IChargerActionLog'
import type { IAuthorisationFilterParams } from '@/models/Authorisation/Filtering/IAuthorisationFilterParams'
import type { IAuthorisation } from '@/models/Authorisation/IAuthorisation'
import type { ICertificateFilterParams } from '@/models/Chargers/Certificate/Filtering/ICertificateFilterParams'
import type { ICertificatesList } from '@/models/Chargers/Certificate/ICertificatesList'
import type { IOcpp16ConfigurationFilterParams } from '@/models/Chargers/Configuration/Filtering/IOcpp16ConfigurationFilterParams'
import type { IOcpp201ConfigurationFilterParams } from '@/models/Chargers/Configuration/Filtering/IOcpp201ConfigurationFilterParams'
import type { IOcpp16Configuration } from '@/models/Chargers/Configuration/IOcpp16Configuration'
import type { IOcpp16UpdateConfigurationRequest } from '@/models/Chargers/Configuration/IOcpp16UpdateConfigurationRequest'
import type { IOcpp16UpdateConfigurationResponse } from '@/models/Chargers/Configuration/IOcpp16UpdateConfigurationResponse'
import type { IOcpp201Configuration } from '@/models/Chargers/Configuration/IOcpp201Configuration'
import type { IOcpp201UpdateConfigurationRequest } from '@/models/Chargers/Configuration/IOcpp201UpdateConfigurationRequest'
import type { IOcpp201UpdateConfigurationResponse } from '@/models/Chargers/Configuration/IOcpp201UpdateConfigurationResponse'
import type { IRefreshConfigurationOcpp16Response } from '@/models/Chargers/Configuration/IRefreshConfigurationOcpp16Response'
import type { IRefreshConfigurationOcpp201Response } from '@/models/Chargers/Configuration/IRefreshConfigurationOcpp201Response'
import type { IUpdateConnectorStaticStatus } from '@/models/Chargers/Connector/IUpdateConnectorStaticStatus'
import type { IActionResponse } from '@/models/Chargers/IActionResponse'
import type { IChangeAvailabilityRequest } from '@/models/Chargers/IChangeAvailabilityRequest'
import type { IChargerManuallyFaulted } from '@/models/Chargers/IChargerManuallyFaulted'
import type { IChargerPriceSheets } from '@/models/Chargers/IChargerPriceSheets'
import type { IChargerTriggerMessageRequest } from '@/models/Chargers/IChargerTriggerMessageRequest'
import type { IRemoteStartSessionRequest } from '@/models/Chargers/IRemoteStartSessionRequest'
import type { IRemoteStartSessionResponse } from '@/models/Chargers/IRemoteStartSessionResponse'
import type { ITriggerMessageResponse } from '@/models/Chargers/ITriggerMessageResponse'
import type { IUnlockConnectorRequest } from '@/models/Chargers/IUnlockConnectorRequest'
import type { IUnlockConnectorResponse } from '@/models/Chargers/IUnlockConnectorResponse'
import type { DynamicLinqFilter } from '@/models/Filtering/DynamicLinqFilter'
import type { ExcelExportFilter, IExcelColumn } from '@/models/Filtering/ExcelExportFilter'
import type { ICharger } from '@/models/ICharger'
import type { INearbyLocation } from '@/models/INearbyLocation'
import type { IOperationResult } from '@/models/IOperationResult'
import type { IMessageLogRequest } from '@/models/MessageLog/Filtering/IMessageLogsRequest'
import type { IMessageLogDetails } from '@/models/MessageLog/IMessageLogDetails'
import type { IMessageLogsMinimal } from '@/models/MessageLog/IMessageLogsMinimal'
import type { INfcLogRequest } from '@/models/Nfc/INfcLogRequest'
import type { INfcLogResponse } from '@/models/Nfc/INfcLogResponse'
import type { IContentPage } from '@/models/Pagination/IContentPage'
import type { IPageResponse } from '@/models/Pagination/IPageResponse'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import type { IApiRequestOptions } from '../IApiRequestOptions'
import { SERVICES_TYPES } from '../ServicesTypes'
import type IChargerService from './IChargerService'

@injectable()
export class ChargerService implements IChargerService {
  private readonly apiClient: IApiClient
  private readonly chargerBaseUri: string = 'chargers'

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  public async getAllChargers(pageNumber: number = 1, pageSize: number = 30, abortController?: AbortController): Promise<IPaginatedResponse<ICharger>> {
    return this.apiClient.get<IPaginatedResponse<ICharger>>(`${this.chargerBaseUri}?pageNumber=${pageNumber}&pageSize=${pageSize}`, abortController)
  }

  public async getChargerById(id: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<ICharger> {
    return this.apiClient.get<ICharger>(`${this.chargerBaseUri}/${id}`, abortController, options)
  }

  public async searchChargers(filter: string, pageNumber: number = 1, pageSize: number = 30, abortController?: AbortController): Promise<IPaginatedResponse<ICharger>> {
    return this.apiClient.get<IPaginatedResponse<ICharger>>(`${this.chargerBaseUri}/filter/${filter}?pageNumber=${pageNumber}&pageSize=${pageSize}`, abortController)
  }

  public async filterChargers(filter: DynamicLinqFilter, pageNumber: number = 1, pageSize: number = 30, abortController?: AbortController): Promise<IPaginatedResponse<ICharger>> {
    return this.apiClient.post<IPaginatedResponse<ICharger>>(`${this.chargerBaseUri}/filter?pageNumber=${pageNumber}&pageSize=${pageSize}`, filter, abortController)
  }

  public async getChargerRates(id: string): Promise<IChargerPriceSheets> {
    return this.apiClient.get<IChargerPriceSheets>(`${this.chargerBaseUri}/${id}/price-sheets`)
  }

  public async softResetCharger(id: string): Promise<IActionResponse> {
    return this.apiClient.postNoBody<IActionResponse>(`${this.chargerBaseUri}/${id}/soft-reset`)
  }

  public async hardResetCharger(id: string): Promise<IActionResponse> {
    return this.apiClient.postNoBody<IActionResponse>(`${this.chargerBaseUri}/${id}/hard-reset`)
  }

  public async simDeactivateReactivate(id: string): Promise<undefined> {
    return this.apiClient.postNoBody<undefined>(`${this.chargerBaseUri}/${id}/sim-deactivate-activate`)
  }

  public async modemReset(id: string): Promise<undefined> {
    return this.apiClient.postNoBody<undefined>(`${this.chargerBaseUri}/${id}/modem-reset`)
  }

  public async changeAvailability(id: string, request: IChangeAvailabilityRequest): Promise<IActionResponse> {
    return this.apiClient.post<IActionResponse>(`${this.chargerBaseUri}/${id}/change-availability`, request)
  }

  public async unlockConnector(id: string, request: IUnlockConnectorRequest): Promise<IUnlockConnectorResponse> {
    return this.apiClient.post<IUnlockConnectorResponse>(`${this.chargerBaseUri}/${id}/remote-unlock-connector`, request)
  }

  public async remoteStartSession(id: string, request: IRemoteStartSessionRequest): Promise<IRemoteStartSessionResponse> {
    return this.apiClient.post<IRemoteStartSessionResponse>(`${this.chargerBaseUri}/${id}/remote-start-session`, request)
  }

  public async remoteStopSession(sessionId: string): Promise<undefined> {
    return this.apiClient.postNoBody<undefined>(`${this.chargerBaseUri}/${sessionId}/remote-stop-session`)
  }

  public async clearCache(id: string): Promise<IActionResponse> {
    return this.apiClient.postNoBody<IActionResponse>(`${this.chargerBaseUri}/${id}/clear-cache`)
  }

  public async outOfService(id: string, request: IChargerManuallyFaulted): Promise<undefined> {
    return this.apiClient.post<undefined>(`${this.chargerBaseUri}/${id}/update-manually-faulted`, request)
  }

  public async triggerMessage(id: string, request: IChargerTriggerMessageRequest): Promise<ITriggerMessageResponse> {
    return this.apiClient.post<ITriggerMessageResponse>(`${this.chargerBaseUri}/${id}/trigger-message`, request)
  }

  public async deleteCharger(id: string): Promise<undefined> {
    return this.apiClient.delete(`${this.chargerBaseUri}/${id}`)
  }

  public async getConfigurationForOcpp16(
    id: string,
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending: boolean = true,
    filterParams?: IOcpp16ConfigurationFilterParams
  ): Promise<IPageResponse<IOcpp16Configuration>> {
    const queryString = buildQueryParams({
      PageSize: paginationOptions.PageSize,
      PageIndex: paginationOptions.PageNumber,
      SortColumn: sortField,
      SortOrder: isAscending ? 'Ascending' : 'Descending',
      Key: filterParams?.key,
      Value: filterParams?.value
    })
    return this.apiClient.get<IPageResponse<IOcpp16Configuration>>(`${this.chargerBaseUri}/${id}/configuration${queryString}`)
  }

  public async getConfigurationForOcpp201(
    id: string,
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending: boolean = true,
    filterParams?: IOcpp201ConfigurationFilterParams
  ): Promise<IPageResponse<IOcpp201Configuration>> {
    const queryString = buildQueryParams({
      PageSize: paginationOptions.PageSize,
      PageIndex: paginationOptions.PageNumber,
      SortColumn: sortField,
      SortOrder: isAscending ? 'Ascending' : 'Descending',
      ComponentName: filterParams?.componentName,
      ComponentInstance: filterParams?.componentInstance,
      VariableName: filterParams?.variableName,
      VariableInstance: filterParams?.variableInstance,
      EvseId: filterParams?.evseId,
      ConnectorId: filterParams?.connectorId,
      DataType: filterParams?.dataType
    })
    return this.apiClient.get<IPageResponse<IOcpp201Configuration>>(`${this.chargerBaseUri}/${id}/configuration${queryString}`)
  }

  public async refreshConfigurationForOcpp16(id: string): Promise<IRefreshConfigurationOcpp16Response> {
    return this.apiClient.get<IRefreshConfigurationOcpp16Response>(`${this.chargerBaseUri}/${id}/refresh-configuration`)
  }

  public async refreshConfigurationForOcpp201(id: string): Promise<IRefreshConfigurationOcpp201Response> {
    return this.apiClient.get<IRefreshConfigurationOcpp201Response>(`${this.chargerBaseUri}/${id}/refresh-configuration`)
  }

  public async updateConfigurationForOcpp16(id: string, request: IOcpp16UpdateConfigurationRequest): Promise<IOcpp16UpdateConfigurationResponse> {
    return this.apiClient.put<IOcpp16UpdateConfigurationResponse>(`${this.chargerBaseUri}/${id}/update-configuration`, request)
  }

  public async updateConfigurationForOcpp201(id: string, request: IOcpp201UpdateConfigurationRequest): Promise<IOcpp201UpdateConfigurationResponse> {
    return this.apiClient.put<IOcpp201UpdateConfigurationResponse>(`${this.chargerBaseUri}/${id}/update-configuration`, request)
  }

  public async exportConfigurationForOcpp16(
    chargerid: string,
    size: number,
    columns: IExcelColumn[],
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending?: boolean,
    filterParams?: IOcpp16ConfigurationFilterParams
  ): Promise<{ blob: Blob; fileName?: string }> {
    const queryString = buildQueryParams({
      PageSize: paginationOptions.PageSize,
      PageIndex: paginationOptions.PageNumber,
      SortColumn: sortField,
      SortOrder: isAscending ? 'Ascending' : 'Descending',
      Key: filterParams?.key,
      Value: filterParams?.value
    })
    return await this.apiClient.postBlobWithFileName(`${this.chargerBaseUri}/${chargerid}/configuration/download/${size}${queryString}`, columns)
  }

  public async exportConfigurationForOcpp2(
    chargerid: string,
    size: number,
    columns: IExcelColumn[],
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending?: boolean,
    filterParams?: IOcpp201ConfigurationFilterParams
  ): Promise<{ blob: Blob; fileName?: string }> {
    const queryString = buildQueryParams({
      PageSize: paginationOptions.PageSize,
      PageIndex: paginationOptions.PageNumber,
      SortColumn: sortField,
      SortOrder: isAscending ? 'Ascending' : 'Descending',
      ComponentName: filterParams?.componentName,
      ComponentInstance: filterParams?.componentInstance,
      VariableName: filterParams?.variableName,
      VariableInstance: filterParams?.variableInstance,
      EvseId: filterParams?.evseId,
      ConnectorId: filterParams?.connectorId,
      DataType: filterParams?.dataType
    })
    return await this.apiClient.postBlobWithFileName(`${this.chargerBaseUri}/${chargerid}/configuration/download/${size}${queryString}`, columns)
  }

  public async getMessageLogs(request: IMessageLogRequest): Promise<IContentPage<IMessageLogsMinimal>> {
    const queryParam = request ? toQueryParams<IMessageLogRequest>(request) : ''
    return this.apiClient.get<IContentPage<IMessageLogsMinimal>>(`chargers/${request.ChargePointId}/message-logs?${queryParam}`)
  }

  public async getMessageLogsTypes(protocol: string = 'ALL'): Promise<string[]> {
    return this.apiClient.get<string[]>(`message-logs-types?protocol=${protocol}`)
  }

  public async getMessageLogsDetails(chargePointId: string, messageLogId: string): Promise<IMessageLogDetails> {
    return this.apiClient.get<IMessageLogDetails>(`chargers/${chargePointId}/message-logs/${messageLogId}`)
  }

  public async getActionLogs(
    chargePointId: string,
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending: boolean = true,
    filterParams?: IChargerActionLogFilterParams
  ): Promise<IContentPage<IChargerActionLogs>> {
    const queryString = buildQueryParams({
      pageIndex: paginationOptions.PageNumber,
      pageSize: paginationOptions.PageSize,
      sortColumn: sortField,
      sortReversed: !isAscending,
      dateFrom: filterParams?.DateFrom,
      dateUntil: filterParams?.DateUntil,
      username: filterParams?.Username,
      chargePointId: filterParams?.ChargePointId,
      connector: filterParams?.Connector,
      evseId: filterParams?.EvseId,
      sessionId: filterParams?.SessionId,
      actionLogAction: filterParams?.ActionLogAction?.toString(),
      parmeters: filterParams?.Parameters,
      status: filterParams?.Status
    })
    return this.apiClient.get<IContentPage<IChargerActionLogs>>(`chargers/${chargePointId}/action-logs${queryString}`)
  }

  public async getCertificates(
    id: string,
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending: boolean = true,
    filterParams?: ICertificateFilterParams
  ): Promise<ICertificatesList> {
    const queryString = buildQueryParams({
      serialNumber: filterParams?.SerialNumber,
      certificateType: filterParams?.CertificateType,
      issuer: filterParams?.Issuer,
      subject: filterParams?.Subject,
      commonName: filterParams?.CommonName,
      pageSize: paginationOptions.PageSize,
      pageIndex: paginationOptions.PageNumber - 1,
      isValid: filterParams?.IsValid,
      sortColumn: sortField,
      sortOrder: isAscending ? 'ascending' : 'descending'
    })
    return this.apiClient.get<ICertificatesList>(`chargers/${id}/certificates${queryString}`)
  }

  public async getAuthorisations(
    chargePointId: string,
    paginationOptions: IPaginationOptions,
    sortField?: string,
    isAscending: boolean = true,
    filterParams?: IAuthorisationFilterParams,
    abortController?: AbortController
  ): Promise<IContentPage<IAuthorisation>> {
    const queryString = buildQueryParams({
      pageIndex: paginationOptions.PageNumber,
      pageSize: paginationOptions.PageSize,
      sortColumn: sortField,
      sortReversed: !isAscending,
      authorisationReference: filterParams?.AuthorisationReference,
      dateFrom: filterParams?.DateFrom,
      dateUntil: filterParams?.DateUntil,
      externalSessionId: filterParams?.ExternalSessionId,
      initiator: filterParams?.Initiator?.toString(),
      chargePointId: filterParams?.ChargePointId,
      tokenId: filterParams?.TokenId,
      visualId: filterParams?.VisualId,
      tokenType: filterParams?.TokenType?.toString(),
      evcoId: filterParams?.EvcoId,
      isAuthorised: filterParams?.IsAuthorised
    })
    return this.apiClient.get<IContentPage<IAuthorisation>>(`chargers/${chargePointId}/authorisations${queryString}`, abortController)
  }

  public async installCertificate(id: string, certificateType: InstallCertificateType) {
    return this.apiClient.post<string>(`chargers/${id}/install-certificate`, { certificateType })
  }

  public async getNfcLogs(chargePointId: string, filterParams?: INfcLogRequest): Promise<INfcLogResponse> {
    const queryString = filterParams ? toQueryParams<INfcLogRequest>(filterParams) : ''
    return this.apiClient.get<INfcLogResponse>(`chargers/${chargePointId}/nfc-logs?${queryString}`)
  }

  public async getNearbyChargers(id: string, radius: number = 10): Promise<INearbyLocation[]> {
    return this.apiClient.get<INearbyLocation[]>(`${this.chargerBaseUri}/${id}/nearby-chargers?radius=${radius}`)
  }

  public async exportChargers(filter: ExcelExportFilter, pageNumber: number, pageSize: number, size: number, abortController?: AbortController): Promise<{ blob: Blob; fileName?: string }> {
    return this.apiClient.postBlobWithFileName(`${this.chargerBaseUri}/download/${size}?pageNumber=${pageNumber}&pageSize=${pageSize}`, filter, abortController)
  }

  public async updateStaticStatus(id: string, staticStatuses: IUpdateConnectorStaticStatus, abortController?: AbortController): Promise<IOperationResult> {
    try {
      await this.apiClient.post(`${this.chargerBaseUri}/${id}/update-connector-dynamics-cpm-status`, staticStatuses, abortController)
      return { Success: true }
    } catch {
      return { Success: false }
    }
  }
}
